<div class="iv" [class.iv--expanded]="expanded">

  <!-- ════════════════════════════════════════════════════
       LOADING STATE — skeleton row
  ════════════════════════════════════════════════════ -->
  @if (loadingList) {
    <div class="iv-row iv-row--skeleton">
      <div class="iv-skeleton iv-skeleton--btn"></div>
      <div class="iv-skeleton iv-skeleton--id"></div>
      <div class="iv-skeleton iv-skeleton--chip"></div>
      <div class="iv-skeleton iv-skeleton--dots"></div>
      <div class="iv-skeleton iv-skeleton--date"></div>
    </div>
  }

  <!-- ════════════════════════════════════════════════════
       EMPTY STATE
  ════════════════════════════════════════════════════ -->
  @if (!loadingList && !verifications.length) {
    <div class="iv-row iv-row--empty">
      <mat-icon class="iv-empty__icon">verified_user</mat-icon>
      <span class="iv-empty__text">No identity verifications</span>
      <button mat-stroked-button color="primary" class="iv-start-btn" (click)="startVerification()">
        <mat-icon>add</mat-icon> Start Verification
      </button>
    </div>
  }

  <!-- ════════════════════════════════════════════════════
       MAIN CONTENT
  ════════════════════════════════════════════════════ -->
  @if (!loadingList && verifications.length) {

    <!-- ── Collapsed row (always visible) ── -->
    <div class="iv-row" [class.iv-row--loading]="loadingDetail">

      <!-- Navigation -->
      <div class="iv-nav">
        <button class="iv-nav__trigger"
                [matMenuTriggerFor]="verificationMenu"
                [disabled]="!verifications.length">
          <span class="iv-nav__label">
            <b>{{ currentIndex + 1 }}</b>
            <span class="iv-nav__sep">/</span>
            <span class="iv-nav__total">{{ total }}</span>
          </span>
          <mat-icon class="iv-nav__chevron">expand_more</mat-icon>
        </button>

        <mat-menu #verificationMenu="matMenu" class="iv-nav-menu">
          @for (v of verifications; track v.id; let i = $index) {
            <button mat-menu-item
                    class="iv-nav-menu__item"
                    [class.iv-nav-menu__item--active]="i === currentIndex"
                    (click)="selectVerification(i)">
              <span class="iv-nav-menu__row">
                <span class="iv-mono">#{{ v.id }}</span>
                <span class="iv-chip iv-chip--{{ statusClass(v.status) }} iv-chip--xs">
                  {{ v.status }}
                </span>
                <span class="iv-nav-menu__date">{{ v.startedAt | date:'dd.MM.yy HH:mm' }}</span>
              </span>
            </button>
          }
        </mat-menu>
      </div>

      <div class="iv-sep"></div>

      @if (currentDetail) {
        @if (currentIsActive) {
          <mat-menu #statusMenu="matMenu">
            <div class="iv-menu-header">Change status</div>
            <button mat-menu-item (click)="decide('complete')">
              <mat-icon>check_circle</mat-icon>
              <span>Complete</span>
            </button>
            <button mat-menu-item (click)="decide('reject')">
              <mat-icon>cancel</mat-icon>
              <span>Reject</span>
            </button>
          </mat-menu>

          <div class="iv-chip iv-chip--{{ statusClass(currentDetail.status) }} iv-chip--actionable"
              [matMenuTriggerFor]="statusMenu"
              matTooltip="Change status"
              matTooltipPosition="above">
            <mat-icon class="iv-chip__icon">{{ STATUS_ICON[currentDetail.status] ?? 'help_outline' }}</mat-icon>
            {{ currentDetail.status }}
            <mat-icon class="iv-chip__icon iv-chip__arrow">arrow_drop_down</mat-icon>
          </div>

        } @else {
          <div class="iv-chip iv-chip--{{ statusClass(currentDetail.status) }}">
            <mat-icon class="iv-chip__icon">{{ STATUS_ICON[currentDetail.status] ?? 'help_outline' }}</mat-icon>
            {{ currentDetail.status }}
          </div>
        }
      } @else {
        <span class="iv-id iv-id--ghost">—</span>
      }

      <div class="iv-sep"></div>

      @if (currentDetail) {
        <span class="iv-meta">{{ currentDetail.startedAt | date:'dd.MM.yyyy HH:mm' }}</span>
      }

      <!-- Spacer pushes right-side actions to the edge -->
      <span class="iv-spacer"></span>

      <itfg-mat-tab-menu class="iv__menu-btn">
        <div trigger>
          <mat-icon>more_horiz</mat-icon>
        </div>
        <div content>
          <button mat-menu-item  (click)="startVerification()">
            <mat-icon>add</mat-icon> Start new
          </button>
        </div>
      </itfg-mat-tab-menu>

      <!-- Expand toggle -->
      <button
        mat-icon-button
        class="iv-expand-btn"
        [class.iv-expand-btn--open]="expanded"
        (click)="toggleExpanded()"
        [matTooltip]="expanded ? 'Hide details' : 'Show details'">
        <mat-icon>expand_more</mat-icon>
      </button>

    </div>

    <!-- ── Expandable details panel ── -->
    <div class="iv-panel" [class.iv-panel--open]="expanded">
      <div class="iv-panel__inner">

        @if (loadingDetail) {
          <div class="iv-panel__loading">
            <mat-spinner diameter="22" />
          </div>
        }

        @if (!loadingDetail && currentDetail) {

          <!-- Events + session -->
          <div class="iv-body">

            <div class="iv-events-col">
              <span class="iv-label">Events</span>
              <div class="iv-timeline">
                @for (ev of events; track ev.id; let last = $last) {
                  <div
                    class="iv-event"
                    [class.iv-event--last]="last && selectedEventId !== ev.id"
                    [class.iv-event--active]="selectedEventId === ev.id"
                    [class.iv-event--clickable]="ev.data"
                    (click)="ev.data && toggleEvent(ev.id)">

                    <div class="iv-event__dot" [style.background]="
                      isFailEvent(ev.type)    ? '#e53935' :
                      isSuccessEvent(ev.type) ? '#43a047' :
                      procColor(ev.processor)">
                    </div>

                    <span class="iv-event__type"
                          [class.iv-event__type--fail]="isFailEvent(ev.type)"
                          [class.iv-event__type--ok]="isSuccessEvent(ev.type)">
                      {{ ev.type.split('_').join(' ') }}
                    </span>

                    <span class="iv-proc-tag"
                          [style.background]="procColor(ev.processor) + '1a'"
                          [style.color]="procColor(ev.processor)">
                      {{ ev.processor }}
                    </span>
                    <span class="iv-mono iv-muted iv-event__ts">
                      {{ ev.createdAt | date:'dd.MM.yyyy HH:mm:ss' }}
                    </span>

                    @if (ev.data) {
                      <mat-icon class="iv-event__chevron">
                        {{ selectedEventId === ev.id ? 'expand_less' : 'expand_more' }}
                      </mat-icon>
                    }

                  </div>

                  <!-- ── Event data drawer ── -->
                  @if (ev.data && selectedEventId === ev.id) {
                    <div class="iv-event-data" [class.iv-event-data--last]="last">
                      @for (entry of eventDataEntries(ev); track entry[0]) {
                        <div class="iv-event-data__row">
                          <span class="iv-event-data__key">{{ entry[0] }}</span>
                          <span class="iv-event-data__val iv-mono">{{ entry[1] }}</span>
                        </div>
                      }
                      @if (!eventDataEntries(ev).length) {
                        <span class="iv-event-data__empty">No data</span>
                      }
                    </div>
                  }
                }
              </div>
            </div>

            <div class="iv-sidebar">
              <div class="iv-field"><span>ID</span>       <span class="iv-mono">#{{ currentDetail.id }}</span></div>
              <div class="iv-field"><span>Flow</span>     <span class="iv-mono">{{ currentDetail.flow }}</span></div>
              <div class="iv-field"><span>Started</span>  <span>{{ currentDetail.startedAt | date:'dd.MM.yy HH:mm' }}</span></div>
              <div class="iv-field"><span>Ended</span>    <span>{{ (currentDetail.endedAt | date:'dd.MM.yy HH:mm') ?? '—' }}</span></div>
              <div class="iv-field"><span>Duration</span> <span>{{ duration(currentDetail.startedAt, currentDetail.endedAt) }}</span></div>
              @if (currentDetail.miReportId) {
                <div class="iv-field"><span>MI report</span> <span class="iv-mono">#{{ currentDetail.miReportId }}</span></div>
              }
            </div>

          </div>
        }

      </div>
    </div>

  }

</div>